import { useCallback, useMemo, useState } from 'react';

import { useIntl } from 'react-intl';

import { useSendUpdateUserAttributesEventMutation } from 'generated/graphql-gateway';
import { useAuth } from 'hooks/auth/use-auth';
import { useCdpContext } from 'state/cdp';
import { CustomEventNames, EventTypes } from 'state/cdp/constants';
import { useLocationContext } from 'state/location';
import { useStoreContext } from 'state/store';
import { toast } from 'utils/toast';

export function useFavoriteOffers() {
  const { user } = useAuth();
  const { updateUserFavOffers, isAuthenticated } = useAuth();
  const { formatMessage } = useIntl();
  const { updateUserAttributes, trackEvent } = useCdpContext();
  const [sendUpdateUserAttributesEvent] = useSendUpdateUserAttributesEventMutation();
  const { location } = useLocationContext();

  const [loading, setLoading] = useState<string[]>([]);
  const { store } = useStoreContext();

  const favoriteOffers = useMemo(() => {
    const userFavoriteOffers = user?.details?.favoriteOffers || [];
    return userFavoriteOffers.filter(offer => !!offer.id);
  }, [user?.details?.favoriteOffers]);

  const isFavorite = useCallback(
    (offerId: string) => !!favoriteOffers.find(offer => offer.id === offerId),
    [favoriteOffers]
  );

  const toggleFavorite = async (sanityId: string, name: string) => {
    if (!isAuthenticated()) {
      return;
    }

    let newFavorites = [];
    let message = '';
    let eventName = '';

    setLoading([...loading, sanityId]);

    if (favoriteOffers.find(offer => offer.id === sanityId)) {
      newFavorites = favoriteOffers.filter(offer => offer.id !== sanityId);
      message = formatMessage({ id: 'offerFavoriteRemoved' });
      eventName = CustomEventNames.OFFER_FAVORITE_REMOVED;
    } else {
      newFavorites = [...favoriteOffers, { id: sanityId }];
      message = formatMessage({ id: 'offerFavoriteAdded' });
      eventName = CustomEventNames.OFFER_FAVORITE_ADDED;
    }

    trackEvent({
      name: eventName as CustomEventNames,
      type: EventTypes.UserPreference,
      attributes: {
        sanityId,
        name,
        userId: user?.cognitoId,
        storeId: store?._id,
        path: location.pathname,
      },
    });

    await updateUserFavOffers(newFavorites);

    updateUserAttributes(
      {
        favoriteOffers: JSON.stringify(favoriteOffers.map(offer => offer.id)),
      },
      {},
      sendUpdateUserAttributesEvent
    );

    toast.success(message);

    setLoading(prevState => prevState.filter(offer => offer !== sanityId));
  };

  const isOfferLoading = useCallback((offerId: string) => loading.includes(offerId), [loading]);

  return {
    favoriteOffers,
    toggleFavorite,
    isFavorite,
    isOfferLoading,
  };
}
